import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.heat';
import { toast } from 'react-toastify';
import geospatialService from '../../services/geospatialService';
import ImageViewer from '../common/ImageViewer';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MapLayers = ({ geoData, heatmapData, layers, analysisMode, setAnalysisResult }) => {
  const map = useMap();
  const markersLayerRef = useRef(null);
  const clusterLayerRef = useRef(null);
  const heatmapLayerRef = useRef(null);
  const analysisLayerRef = useRef(null);

  const { isAuthenticated, role } = useSelector((state) => state.auth);
  
  // Status color mapping
  const getStatusColor = (status) => {
    const statusColors = {
      'pending': '#FFA500',
      'approved': '#4CAF50',
      'rejected': '#F44336',
      'in_progress': '#2196F3',
      'completed': '#8BC34A',
    };
    return statusColors[status] || '#757575';
  };
  
  // Create custom icon based on status
  const createCustomIcon = (status) => {
    const color = getStatusColor(status);
    return L.divIcon({
      html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
      className: 'custom-marker',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });
  };
  
  // Create popup content
  const createPopupContent = (properties) => {
    return `
      <div style="min-width: 200px;">
        <h3 style="margin: 0 0 10px 0; color: #1976d2;">Application ${properties.application_refno || properties.application_id}</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 4px 8px; font-weight: bold;">Applicant:</td>
            <td style="padding: 4px 8px;">${properties.applicant_name || 'N/A'}</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: bold;">Phone No.:</td>
            <td style="padding: 4px 8px;">${properties.contact_number || 'N/A'}</td>
          </tr>
          <tr style="background-color: #f5f5f5;">
            <td style="padding: 4px 8px; font-weight: bold;">Status:</td>
            <td style="padding: 4px 8px;">
              <span style="background-color: ${getStatusColor(properties.status)}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px;">
                ${properties.status}
              </span>
            </td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: bold;">Damage Type:</td>
            <td style="padding: 4px 8px;">${properties.for_damage_type || 'N/A'}</td>
          </tr>
          <tr style="background-color: #f5f5f5;">
            <td style="padding: 4px 8px; font-weight: bold;">NC Date:</td>
            <td style="padding: 4px 8px;">${properties.actual_nc_date || 'N/A'}</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: bold;">App Date:</td>
            <td style="padding: 4px 8px;">${properties.application_date || 'N/A'}</td>
          </tr>
          <tr style="background-color: #f5f5f5;">
            <td style="padding: 4px 8px; font-weight: bold;">Report ID:</td>
            <td style="padding: 4px 8px;">${properties.report_id}</td>
          </tr>
        </table>
        ${properties.captured_images ? `<div style="margin-top: 10px;"><strong>Images Available</strong></div>` : ''}
        <div style="margin-top: 10px;">
          <button 
            id="view-application-${properties.application_refno}"
            style="
              width: 100%;
              padding: 8px;
              background-color: #1976d2;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-weight: bold;
            "
          >
            View Application Details
          </button>
        </div>
      </div>
    `;
  };
  
  // Handle view application button click
  const handleViewApplication = (applicationRefNo) => {
    // Navigate to application details page
    window.open(`/applications/details/${applicationRefNo}`, '_blank');
  };
  
  // Handle marker click for analysis mode
  const handleMarkerClick = (latlng) => {
    if (analysisMode === 'buffer') {
      // Perform buffer analysis
      performBufferAnalysis(latlng);
    } else if (analysisMode === 'distance' && analysisLayerRef.current) {
      // Handle distance measurement
      const markers = analysisLayerRef.current.getLayers();
      if (markers.length === 0) {
        // First point
        const marker = L.marker(latlng, {
          icon: L.divIcon({
            html: '<div style="background-color: #FF0000; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white;"></div>',
            className: 'distance-marker',
            iconSize: [12, 12],
            iconAnchor: [6, 6],
          })
        });
        analysisLayerRef.current.addLayer(marker);
      } else if (markers.length === 1) {
        // Second point - calculate distance
        const marker = L.marker(latlng, {
          icon: L.divIcon({
            html: '<div style="background-color: #FF0000; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white;"></div>',
            className: 'distance-marker',
            iconSize: [12, 12],
            iconAnchor: [6, 6],
          })
        });
        analysisLayerRef.current.addLayer(marker);
        
        const point1 = markers[0].getLatLng();
        const point2 = latlng;
        
        // Draw line
        const line = L.polyline([point1, point2], { color: 'red', weight: 2, dashArray: '5, 10' });
        analysisLayerRef.current.addLayer(line);
        
        // Calculate distance
        calculateDistance(point1, point2);
      }
    }
  };
  
  // Perform buffer analysis
  const performBufferAnalysis = async (latlng) => {
    try {
      const result = await geospatialService.bufferAnalysis(latlng.lat, latlng.lng, 5000);
      setAnalysisResult(result);
      
      // Draw buffer circle
      const circle = L.circle(latlng, {
        radius: 5000,
        color: '#2196F3',
        fillColor: '#2196F3',
        fillOpacity: 0.2
      });
      analysisLayerRef.current.clearLayers();
      analysisLayerRef.current.addLayer(circle);
      
    } catch (error) {
      console.error('Buffer analysis error:', error);
      toast.error('Failed to perform buffer analysis');
    }
  };
  
  // Calculate distance between two points
  const calculateDistance = async (point1, point2) => {
    try {
      const result = await geospatialService.calculateDistance(
        { lat: point1.lat, lng: point1.lng },
        { lat: point2.lat, lng: point2.lng }
      );
      setAnalysisResult(result);
      
    } catch (error) {
      console.error('Distance calculation error:', error);
      toast.error('Failed to calculate distance');
    }
  };
  
  // Initialize layers
  useEffect(() => {
    if (!map) return;
    
    // Initialize marker layer group
    if (!markersLayerRef.current) {
      markersLayerRef.current = L.layerGroup().addTo(map);
    }
    
    // Initialize cluster layer
    if (!clusterLayerRef.current) {
      clusterLayerRef.current = L.markerClusterGroup({
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        iconCreateFunction: function(cluster) {
          const childCount = cluster.getChildCount();
          let size = 'small';
          if (childCount > 10) size = 'medium';
          if (childCount > 50) size = 'large';
          
          return L.divIcon({
            html: `<div><span>${childCount}</span></div>`,
            className: `marker-cluster marker-cluster-${size}`,
            iconSize: new L.Point(40, 40)
          });
        }
      });
    }
    
    // Initialize heatmap layer
    if (!heatmapLayerRef.current) {
      heatmapLayerRef.current = L.heatLayer([], {
        radius: 30,
        blur: 15,
        maxZoom: 17,
        gradient: {
          0.0: 'blue',
          0.6: 'lime',
          0.7: 'yellow',
          1.0: 'red'
        }
      });
    }
    
    // Initialize analysis layer
    if (!analysisLayerRef.current) {
      analysisLayerRef.current = L.layerGroup().addTo(map);
    }
    
    return () => {
      // Cleanup on unmount
      if (markersLayerRef.current) {
        map.removeLayer(markersLayerRef.current);
      }
      if (clusterLayerRef.current) {
        map.removeLayer(clusterLayerRef.current);
      }
      if (heatmapLayerRef.current) {
        map.removeLayer(heatmapLayerRef.current);
      }
      if (analysisLayerRef.current) {
        map.removeLayer(analysisLayerRef.current);
      }
    };
  }, [map]);
  
  // Update markers and clusters
  useEffect(() => {
    if (!map || !geoData || !geoData.features) return;
    
    // Clear existing layers
    markersLayerRef.current.clearLayers();
    clusterLayerRef.current.clearLayers();
    
    // Add markers
    geoData.features.forEach(feature => {
      if (feature.geometry.type === 'Point') {
        const [lng, lat] = feature.geometry.coordinates;
        const icon = feature.properties && feature.properties.status ? createCustomIcon(feature.properties.status) : new L.Icon.Default();        
        const marker = L.marker([lat, lng], { icon });

        if (feature.properties)
          marker.bindPopup(createPopupContent(feature.properties));
        
        // Add click listener for view application button
        marker.on('popupopen', () => {
          const button = document.getElementById(`view-application-${feature.properties.application_refno}`);
          if (button) {
            button.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleViewApplication(feature.properties.application_refno);
            };
          }
        });
        
        marker.on('click', () => {
          if (analysisMode && isAuthenticated) {
            handleMarkerClick(marker.getLatLng());
          }
        });
        
        // Add to appropriate layer
        if (layers.clusters) {
          clusterLayerRef.current.addLayer(marker);
        } else if (layers.markers) {
          markersLayerRef.current.addLayer(marker);
        }
      }
    });
    
    // Add/remove cluster layer
    if (layers.clusters) {
      map.addLayer(clusterLayerRef.current);
      if (layers.markers && map.hasLayer(markersLayerRef.current)) {
        map.removeLayer(markersLayerRef.current);
      }
    } else if (layers.markers) {
      map.addLayer(markersLayerRef.current);
      if (map.hasLayer(clusterLayerRef.current)) {
        map.removeLayer(clusterLayerRef.current);
      }
    } else {
      map.removeLayer(markersLayerRef.current);
      map.removeLayer(clusterLayerRef.current);
    }
    
    // Fit bounds if we have data
    if (geoData.features.length > 0 && layers.markers || layers.clusters) {
      const bounds = L.geoJSON(geoData).getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
    
  }, [geoData, layers.markers, layers.clusters, map]);
  
  // Update heatmap
  useEffect(() => {
    if (!map || !heatmapLayerRef.current) return;
    
    if (layers.heatmap && heatmapData.length > 0) {
      // Add layer to map first if not already added
      if (!map.hasLayer(heatmapLayerRef.current)) {
        map.addLayer(heatmapLayerRef.current);
      }
      // Then update the data
      heatmapLayerRef.current.setLatLngs(heatmapData);
    } else {
      // Remove layer if it exists on the map
      if (map.hasLayer(heatmapLayerRef.current)) {
        map.removeLayer(heatmapLayerRef.current);
      }
    }
  }, [heatmapData, layers.heatmap, map]);
  
  // Handle analysis mode changes
  useEffect(() => {
    if (!analysisMode && analysisLayerRef.current) {
      analysisLayerRef.current.clearLayers();
    }
  }, [analysisMode]);
  
  // Add map click handler for analysis
  useEffect(() => {
    if (!map) return;
    
    const handleMapClick = (e) => {
      if (analysisMode) {
        handleMarkerClick(e.latlng);
      }
    };
    
    map.on('click', handleMapClick);
    
    return () => {
      map.off('click', handleMapClick);
    };
  }, [map, analysisMode]);
  
  return null; // This component doesn't render anything directly
};

export default MapLayers;
